import React, { useEffect, useState } from 'react';
import { useBillingStore, PaymentMethod, BillingTransaction, Subscription } from '../../store/billingStore';
import { useToastStore } from '../../store/toastStore';
import {
  CreditCard, Plus, Trash2, Star, Wallet, ArrowUpRight,
  Loader2, Receipt, Clock, CheckCircle2, XCircle, RefreshCw,
  ChevronRight, Crown, Sparkles, AlertTriangle,
} from 'lucide-react';
import AddCardModal from './AddCardModal';
import TransactionDetailModal from './TransactionDetailModal';
import SubscriptionPlansModal from './SubscriptionPlansModal';

/* ── Card brand icons (text fallback) ── */
const brandLabel: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
};

const brandColor: Record<string, string> = {
  visa: 'text-blue-400',
  mastercard: 'text-orange-400',
  amex: 'text-cyan-400',
  discover: 'text-amber-400',
};

/* ── Status badge component ── */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; cls: string; Icon: React.ElementType }> = {
    active: { label: 'Actif', cls: 'text-success bg-success/10', Icon: CheckCircle2 },
    completed: { label: 'Complété', cls: 'text-success bg-success/10', Icon: CheckCircle2 },
    pending: { label: 'En attente', cls: 'text-warning bg-warning/10', Icon: Clock },
    incomplete: { label: 'En attente', cls: 'text-warning bg-warning/10', Icon: Clock },
    past_due: { label: 'Impayé', cls: 'text-error bg-error/10', Icon: XCircle },
    failed: { label: 'Échoué', cls: 'text-error bg-error/10', Icon: XCircle },
    refunded: { label: 'Remboursé', cls: 'text-blue-400 bg-blue-400/10', Icon: RefreshCw },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${s.cls}`}>
      <s.Icon className="h-3 w-3" />
      {s.label}
    </span>
  );
};

/* ── Type label ── */
const typeLabel: Record<string, string> = {
  game_purchase: 'Achat de jeu',
  wallet_topup: 'Recharge portefeuille',
  hosting_subscription: 'Hébergement',
  erozion_plus: 'Erozion Plus',
  refund: 'Remboursement',
};

/* ═══════════════════════════════════════════════════════════════════════════ */

const BillingSettingsTab: React.FC = () => {
  const {
    methods, wallet, transactions, transactionsTotal,
    isLoadingMethods, isLoadingWallet, isLoadingTransactions,
    fetchMethods, fetchWallet, fetchTransactions,
    removeMethod, setDefaultMethod,
    subscriptions, fetchSubscriptions,
    cancelSubscription, reactivateSubscription,
    syncBilling,
  } = useBillingStore();
  const { addToast } = useToastStore();
  const [showAddCard, setShowAddCard] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);
  const [showPlans, setShowPlans] = useState(false);
  const [cancelingSubId, setCancelingSubId] = useState<string | null>(null);
  const safeMethods = methods ?? [];
  const safeSubscriptions = subscriptions ?? [];
  const safeTransactions = transactions ?? [];

  useEffect(() => {
    // Sync with Stripe first (catches missed webhooks), then fetch fresh data
    syncBilling();
    fetchMethods();
    fetchWallet();
  }, []);

  /* ── Delete card handler ── */
  const handleRemove = async (pm: PaymentMethod) => {
    if (pm.isDefault) {
      addToast({ type: 'system', title: 'Erreur', body: 'Impossible de supprimer la carte par défaut', duration: 3000 });
      return;
    }
    setRemovingId(pm.id);
    const ok = await removeMethod(pm.id);
    setRemovingId(null);
    if (ok) addToast({ type: 'system', title: 'Carte supprimée', body: `••••${pm.last4} retirée`, duration: 3000 });
  };

  /* ── Set default handler ── */
  const handleSetDefault = async (pm: PaymentMethod) => {
    if (pm.isDefault) return;
    const ok = await setDefaultMethod(pm.id);
    if (ok) addToast({ type: 'system', title: 'Carte par défaut', body: `••••${pm.last4} définie`, duration: 3000 });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* ── Header ── */}
      <div>
        <h1 className="text-xl font-bold text-text-primary">Facturation</h1>
        <p className="text-sm text-text-muted mt-1">Gérez vos moyens de paiement, portefeuille et historique.</p>
      </div>

      {/* ═══════════ Payment Methods ═══════════ */}
      <Section title="Moyens de paiement" icon={CreditCard}>
        {isLoadingMethods ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
          </div>
        ) : (
          <>
            {safeMethods.length === 0 ? (
              <p className="text-sm text-text-muted py-4">Aucune carte enregistrée.</p>
            ) : (
              <div className="space-y-2">
                {safeMethods.map((pm) => (
                  <div
                    key={pm.id}
                    className="flex items-center justify-between px-4 py-3 rounded-xl bg-bg-secondary/60 border border-border-color hover:border-accent-color/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <CreditCard className={`h-5 w-5 ${brandColor[pm.brand] || 'text-text-muted'}`} />
                      <div>
                        <span className="text-sm font-medium text-text-primary">
                          {brandLabel[pm.brand] || pm.brand} ••••{pm.last4}
                        </span>
                        <span className="ml-2 text-xs text-text-muted">
                          {String(pm.expMonth).padStart(2, '0')}/{pm.expYear}
                        </span>
                      </div>
                      {pm.isDefault && (
                        <span className="text-[10px] font-bold text-accent-color bg-accent-color/10 px-2 py-0.5 rounded-full">
                          Par défaut
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {!pm.isDefault && (
                        <button
                          onClick={() => handleSetDefault(pm)}
                          className="p-2 rounded-lg text-text-muted hover:text-warning hover:bg-warning/10 transition-colors"
                          title="Définir par défaut"
                        >
                          <Star className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleRemove(pm)}
                        disabled={removingId === pm.id || pm.isDefault}
                        className="p-2 rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors disabled:opacity-30"
                        title="Supprimer"
                      >
                        {removingId === pm.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowAddCard(true)}
              className="mt-3 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-accent-color/10 text-accent-color text-sm font-medium hover:bg-accent-color/20 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Ajouter une carte
            </button>
          </>
        )}
      </Section>

      {/* ═══════════ Wallet ═══════════ */}
      <Section title="Portefeuille" icon={Wallet}>
        {isLoadingWallet ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
          </div>
        ) : wallet ? (
          <div className="flex items-center justify-between px-4 py-4 rounded-xl bg-bg-secondary/60 border border-border-color">
            <div>
              <p className="text-xs text-text-muted uppercase tracking-wider">Solde</p>
              <p className="text-2xl font-bold text-text-primary mt-0.5">
                {wallet.balance.toFixed(2)}&nbsp;
                <span className="text-sm font-normal text-text-muted">{wallet.currency.toUpperCase()}</span>
              </p>
            </div>
            <button
              onClick={() => {
                useBillingStore.getState().openPaymentDrawer({
                  type: 'wallet_topup',
                  itemName: 'Recharge portefeuille',
                  amount: 10,
                  currency: wallet.currency,
                });
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-accent-color text-black text-sm font-bold hover:bg-accent-hover transition-colors"
            >
              <ArrowUpRight className="h-4 w-4" />
              Recharger
            </button>
          </div>
        ) : null}
      </Section>

      {/* ═══════════ Subscriptions ═══════════ */}
      <Section title="Abonnements" icon={Crown}>
        {safeSubscriptions.length > 0 && (
          <div className="space-y-2 mb-4">
            {safeSubscriptions.map((sub) => {
              const isTrial = sub.status === 'trialing';
              const isCanceling = !!sub.cancel_at_period_end;
              const isPastDue = sub.status === 'past_due';
              const trialEndDate = sub.trial_end ? new Date(sub.trial_end).toLocaleDateString('fr-CA') : null;
              // For trialing subs, period_end may be null/epoch — fall back to trial_end
              const rawPeriodEnd = sub.current_period_end ? new Date(sub.current_period_end) : null;
              const isEpochBug = rawPeriodEnd && rawPeriodEnd.getFullYear() < 2000;
              const periodEndDate = (isEpochBug || !rawPeriodEnd)
                ? (trialEndDate || '—')
                : rawPeriodEnd.toLocaleDateString('fr-CA');

              return (
                <div
                  key={sub.id}
                  className="px-4 py-4 rounded-xl bg-bg-secondary/60 border border-border-color space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-purple-400" />
                      <span className="text-sm font-semibold text-text-primary">
                        {sub.plan_name || sub.type}
                      </span>
                      {isTrial && (
                        <span className="text-[10px] font-bold text-purple-400 bg-purple-400/10 px-2 py-0.5 rounded-full">
                          Essai gratuit
                        </span>
                      )}
                      {isCanceling && (
                        <span className="text-[10px] font-bold text-warning bg-warning/10 px-2 py-0.5 rounded-full">
                          Annulation en cours
                        </span>
                      )}
                      {isPastDue && (
                        <span className="text-[10px] font-bold text-error bg-error/10 px-2 py-0.5 rounded-full">
                          Paiement échoué
                        </span>
                      )}
                    </div>
                    <StatusBadge status={sub.status === 'trialing' ? 'active' : sub.status} />
                  </div>

                  <div className="text-xs text-text-muted">
                    {isTrial && trialEndDate && (
                      <p>Essai se termine le {trialEndDate}</p>
                    )}
                    {isCanceling ? (
                      <p>Se termine le {periodEndDate}</p>
                    ) : (
                      <p>Prochain renouvellement: {periodEndDate}</p>
                    )}
                    {sub.price_monthly != null && (
                      <p className="text-text-secondary font-medium">
                        {Number(sub.price_monthly).toFixed(2)}$ / mois
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    {isCanceling ? (
                      <button
                        onClick={async () => {
                          setCancelingSubId(sub.id);
                          const ok = await reactivateSubscription(sub.id);
                          setCancelingSubId(null);
                          if (ok) addToast({ type: 'system', title: 'Abonnement réactivé', body: 'Votre abonnement continuera normalement.', duration: 3000 });
                        }}
                        disabled={cancelingSubId === sub.id}
                        className="text-xs font-semibold text-accent-color hover:text-accent-hover transition-colors flex items-center gap-1"
                      >
                        {cancelingSubId === sub.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                        Réactiver
                      </button>
                    ) : (
                      <button
                        onClick={async () => {
                          setCancelingSubId(sub.id);
                          const ok = await cancelSubscription(sub.id);
                          setCancelingSubId(null);
                          if (ok) addToast({ type: 'system', title: 'Abonnement annulé', body: `Votre abonnement reste actif jusqu'au ${periodEndDate}.`, duration: 4000 });
                        }}
                        disabled={cancelingSubId === sub.id}
                        className="text-xs font-semibold text-error hover:text-red-400 transition-colors flex items-center gap-1"
                      >
                        {cancelingSubId === sub.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button
          onClick={() => setShowPlans(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-purple-500/10 text-purple-400 text-sm font-medium hover:bg-purple-500/20 transition-colors border border-purple-500/20"
        >
          <Sparkles className="h-4 w-4" />
          Découvrir les abonnements
        </button>
      </Section>

      {/* ═══════════ Transaction History ═══════════ */}
      <Section title="Historique" icon={Receipt}>
        {isLoadingTransactions ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
          </div>
        ) : safeTransactions.length === 0 ? (
          <p className="text-sm text-text-muted py-4">Aucune transaction.</p>
        ) : (
          <>
            <div className="space-y-1">
              {safeTransactions.map((tx) => (
                <button
                  key={tx.id}
                  onClick={() => setSelectedTxId(tx.id)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-bg-secondary/40 transition-colors cursor-pointer text-left"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {tx.item_name || tx.description || typeLabel[tx.type] || tx.type}
                    </p>
                    <p className="text-xs text-text-muted">
                      {new Date(tx.created_at).toLocaleDateString('fr-CA')} · {typeLabel[tx.type] || tx.type}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <StatusBadge status={tx.status} />
                    <span className="text-sm font-semibold text-text-primary tabular-nums">
                      {tx.type === 'refund' ? '+' : '-'}{Number(tx.amount).toFixed(2)}$
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-text-muted" />
                  </div>
                </button>
              ))}
            </div>
            {safeTransactions.length < transactionsTotal && (
              <button
                onClick={() => fetchTransactions(safeTransactions.length)}
                disabled={isLoadingTransactions}
                className="mt-3 flex items-center gap-1 text-sm text-accent-color hover:text-accent-hover transition-colors mx-auto"
              >
                Voir plus <ChevronRight className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}
      </Section>

      {/* ── Add Card Modal ── */}
      {showAddCard && (
        <AddCardModal
          onClose={() => setShowAddCard(false)}
          onSuccess={() => {
            setShowAddCard(false);
            fetchMethods();
            addToast({ type: 'system', title: 'Carte ajoutée', body: 'Votre carte a été enregistrée avec succès', duration: 3000 });
          }}
        />
      )}

      {/* ── Transaction Detail Modal ── */}
      {selectedTxId && (
        <TransactionDetailModal
          txId={selectedTxId}
          onClose={() => setSelectedTxId(null)}
        />
      )}

      {/* ── Subscription Plans Modal ── */}
      {showPlans && (
        <SubscriptionPlansModal
          onClose={() => setShowPlans(false)}
        />
      )}
    </div>
  );
};

/* ── Section wrapper ── */
const Section: React.FC<{
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}> = ({ title, icon: Icon, children }) => (
  <section>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-text-muted" />
      <h2 className="text-sm font-bold text-text-secondary uppercase tracking-wider">{title}</h2>
    </div>
    {children}
  </section>
);

export default BillingSettingsTab;
